import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { authAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const { login, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [oauthState, setOauthState] = useState({
    googleReady: false,
    scriptsLoaded: false
  });
  const [initializationAttempted, setInitializationAttempted] = useState(false);
  const { t } = useTranslation();

  // Google Sign-In configuration
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  // Function to handle credential response
  const handleCredentialResponse = async (response: any) => {
    try {
      // The 'response.credential' is the JWT ID token.
      const id_token = response.credential;

      // Send this token to your back-end for verification using the API service.
      const data = await authAPI.googleLogin(id_token);
      
      if (data.token) {
        localStorage.setItem('token', data.token);
        window.location.href = '/dashboard';
      }
      onSuccess?.();
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  useEffect(() => {
    // Prevent multiple initialization attempts
    if (initializationAttempted) {
      return;
    }
    setInitializationAttempted(true);

    // Set initial state to indicate scripts are being loaded
    setOauthState(prev => ({ ...prev, scriptsLoaded: true }));

    // Set up Google Sign-In when script is ready
    const initializeGoogleSignIn = () => {
      try {
        if (!googleClientId || !((window as any).google && (window as any).google.accounts && (window as any).google.accounts.id)) {
          return;
        }
        
        // Set up global callback function
        (window as any).handleCredentialResponse = handleCredentialResponse;

        // Initialize Google Sign-In
        (window as any).google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleCredentialResponse,
        });

        // Mark as ready to render button
        setOauthState(prev => ({ ...prev, googleReady: true }));

        // Render the button after a short delay to ensure DOM is ready
        setTimeout(() => {
          const buttonContainer = document.getElementById('google-signin-button');
          
          if (buttonContainer) {
            try {
              (window as any).google.accounts.id.renderButton(
                buttonContainer,
                {
                  type: 'standard',
                  size: 'large',
                  theme: 'outline',
                  text: 'sign_in_with',
                  shape: 'rectangular',
                  logo_alignment: 'left',
                }
              );
            } catch (renderError) {
              console.error('Error rendering Google Sign-In button:', renderError);
            }
          }
        }, 100);

      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
      }
    };

    // Use consolidated initialization approach
    const initializeOAuth = () => {
      if (googleClientId) {
        // Use the global helper to wait for Google script
        if ((window as any).onGoogleReady) {
          (window as any).onGoogleReady(initializeGoogleSignIn);
        } else {
          // Fallback if the helper isn't available
          const checkGoogleScript = () => {
            if (typeof (window as any).google !== 'undefined' && 
                typeof (window as any).google.accounts !== 'undefined' && 
                typeof (window as any).google.accounts.id !== 'undefined') {
              initializeGoogleSignIn();
            } else {
              setTimeout(checkGoogleScript, 100);
            }
          };
          checkGoogleScript();
        }

        // Also try direct initialization if Google is already available
        if (typeof (window as any).google !== 'undefined' && 
            typeof (window as any).google.accounts !== 'undefined' && 
            typeof (window as any).google.accounts.id !== 'undefined') {
          setTimeout(() => initializeGoogleSignIn(), 500);
        }
      }
    };

    // Initialize OAuth only if we have at least one client ID
    if (googleClientId) {
      initializeOAuth();
    }
  }, [googleClientId, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
      onSuccess?.();
    } catch (error) {
      // Error is handled by the auth context
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {t('auth.signInTitle')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">
            {t('auth.noAccount')}{' '}
            <Link
              to="/signup"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('header.signUp')}
            </Link>
          </p>
          <div className="mt-4 rounded-md bg-blue-50 dark:bg-blue-900/20 p-4">
            <div className="text-sm text-blue-800 dark:text-blue-200 text-center">
              <strong>Just browsing?</strong> No account needed to blip tours and travel data. 
              Accounts are only required for content creators who want to create and manage tours.
            </div>
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                {t('auth.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                  placeholder={t('auth.email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                {t('auth.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 pl-10 pr-10 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:focus:ring-primary-400 dark:focus:border-primary-400 focus:z-10 sm:text-sm"
                  placeholder={t('auth.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('auth.signingIn') : t('header.signIn')}
            </button>
          </div>
        </form>

        {/* OAuth Sign-In Section - Always render container to prevent layout shifts */}
        {googleClientId && (
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 dark:text-gray-500">{t('auth.orContinueWith')}</span>
              </div>
            </div>

            {/* Google Sign-In */}
            {googleClientId && (
              <div className="mt-6">
                {oauthState.googleReady ? (
                  <div className="flex justify-center">
                    <div 
                      id="g_id_onload"
                      data-client_id={googleClientId}
                      data-callback="handleCredentialResponse"
                    ></div>
                    <div 
                      id="google-signin-button"
                      className="flex justify-center"
                      style={{ minHeight: '40px', minWidth: '240px' }}
                    ></div>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <div 
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400 dark:text-gray-500 flex items-center justify-center"
                      style={{ height: '40px', width: '240px' }}
                    >
                      <span className="text-sm">{t('common.loading')}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginForm;